import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface LanguageStore {
  language: 'pl' | 'en';
  setLanguage: (lang: 'pl' | 'en') => void;
}

export interface CartItem {
  plantId: string;
  plantName: string;
  plantNameEn?: string;
  height: string;
  price: number;
  quantity: number;
  imageUrl: string;
}

interface CartStore {
  items: CartItem[];
  isWholesale: boolean;
  cartPriceMode: 'wholesale' | 'retail' | null;
  addItem: (item: CartItem) => void;
  removeItem: (plantId: string, height: string) => void;
  updateQuantity: (plantId: string, height: string, quantity: number) => void;
  clearCart: () => void;
  setWholesale: (isWholesale: boolean) => void;
}

export const useLanguageStore = create(
  persist<LanguageStore>(
    (set) => ({
      language: 'pl',
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'language-storage',
      version: 1,
      migrate: (persistedState: any) => {
        return {
          language: persistedState.language === 'en' ? 'en' : 'pl'
        };
      }
    }
  )
);

export const useCartStore = create(
  persist<CartStore>(
    (set) => ({
      items: [],
      isWholesale: true,
      cartPriceMode: null,
      addItem: (item) =>
        set((state) => {
          const newMode = state.isWholesale ? 'wholesale' : 'retail';
          const existingItem = state.items.find(
            (i) => i.plantId === item.plantId && i.height === item.height
          );
          if (existingItem) {
            return {
              items: state.items.map((i) =>
                i.plantId === item.plantId && i.height === item.height
                  ? { ...i, quantity: i.quantity + item.quantity }
                  : i
              ),
              cartPriceMode: newMode,
            };
          }
          return {
            items: [...state.items, item],
            cartPriceMode: newMode,
          };
        }),
      removeItem: (plantId, height) =>
        set((state) => {
          const newItems = state.items.filter(
            (item) => !(item.plantId === plantId && item.height === height)
          );
          return {
            items: newItems,
            cartPriceMode: newItems.length === 0 ? null : state.cartPriceMode,
          };
        }),
      updateQuantity: (plantId, height, quantity) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.plantId === plantId && item.height === height
              ? { ...item, quantity }
              : item
          ),
        })),
      clearCart: () => set({ items: [], cartPriceMode: null }),
      setWholesale: (isWholesale) =>
        set((state) => {
          if (state.items.length > 0) {
            return state;
          }
          return { isWholesale };
        }),
    }),
    {
      name: 'cart-storage',
      version: 2,
      migrate: (persistedState: any) => {
        return {
          items: Array.isArray(persistedState.items) ? persistedState.items : [],
          isWholesale: typeof persistedState.isWholesale === 'boolean' ? persistedState.isWholesale : true,
          cartPriceMode: persistedState.cartPriceMode || null,
        };
      }
    }
  )
);
